import { List, Record } from 'immutable'
import { Assignment, IAssignment } from './Assignment'
import { AssociatedContact, IAssociatedContact } from './AssociatedContact'
import { AssociatedListing, IAssociatedListing } from './AssociatedListing'
import { AssociatedProperty, IAssociatedProperty } from './AssociatedProperty'
import { Attachment, IAttachment } from './Attachment'
import {
  BrokeragePipelineCommissionFee,
  IBrokeragePipelineCommissionFee,
} from './BrokeragePipelineCommissionFee'
import {
  BrokeragePipelineProgress,
  IBrokeragePipelineProgress,
} from './BrokeragePipelineProgress'
import { CommissionSplit, ICommissionSplit } from './CommissionSplit'
import { Company, ICompany } from './Company'
import { PipelineStatus } from './enums'
import { ILeasePriceTerm, LeasePriceTerm } from './LeasePriceTerm'
import { IListing, Listing } from './Listing'
import { IPotentialProfit, PotentialProfit } from './PotentialProfit'

export interface IBrokeragePipeline {
  readonly associatedListing: IAssociatedListing
  readonly associatedProperty: IAssociatedProperty
  readonly attachments: List<IAttachment>
  readonly buyerLender: string
  readonly buyers: List<IAssociatedContact>
  readonly buyersTitleCompany?: ICompany
  readonly closingDate: number
  readonly commissionSplit: ICommissionSplit
  readonly creator: string
  readonly criticalDates: List<IAssignment>
  readonly deletedCriticalDates: List<IAssignment>
  readonly earnestMoney: number
  readonly earnestMoneyDepositDate?: number
  readonly earnestMoneyLocation: string
  readonly earnestMoneyWithdrawlDate?: number
  readonly effectiveDateOfOffer?: number
  readonly id: string
  readonly is1031Exchange: boolean
  readonly leasePriceBreakdown: List<ILeasePriceTerm>
  readonly listing: IListing
  readonly listingAgents: List<IAssociatedContact>
  readonly listingFee: IBrokeragePipelineCommissionFee
  readonly name: string
  readonly noListingAgent: boolean
  readonly noSellingAgent: boolean
  readonly potentialProfits: List<IPotentialProfit>
  readonly progress: IBrokeragePipelineProgress
  readonly salesPrice: number
  readonly sellerMortgage: string
  readonly sellers: List<IAssociatedContact>
  readonly sellersTitleCompany?: ICompany
  readonly sellingAgents: List<IAssociatedContact>
  readonly sellingFee: IBrokeragePipelineCommissionFee
  readonly status: PipelineStatus
}

const BrokeragePipelineRecord = Record({
  associatedListing: new AssociatedListing(),
  associatedProperty: new AssociatedProperty(),
  attachments: List<Attachment>(),
  buyerLender: '',
  buyers: List<AssociatedContact>(),
  buyersTitleCompany: undefined,
  closingDate: 0,
  commissionSplit: new CommissionSplit(),
  creator: '',
  criticalDates: List<Assignment>(),
  deletedCriticalDates: List<Assignment>(),
  earnestMoney: 0,
  earnestMoneyDepositDate: undefined,
  earnestMoneyLocation: '',
  earnestMoneyWithdrawlDate: undefined,
  effectiveDateOfOffer: undefined,
  id: '',
  is1031Exchange: false,
  leasePriceBreakdown: List<LeasePriceTerm>(),
  listing: new Listing(),
  listingAgents: List<AssociatedContact>(),
  listingFee: new BrokeragePipelineCommissionFee(),
  name: '',
  noListingAgent: false,
  noSellingAgent: false,
  potentialProfits: List<PotentialProfit>(),
  progress: new BrokeragePipelineProgress(),
  salesPrice: 0,
  sellerMortgage: '',
  sellers: List<AssociatedContact>(),
  sellersTitleCompany: undefined,
  sellingAgents: List<AssociatedContact>(),
  sellingFee: new BrokeragePipelineCommissionFee(),
  status: PipelineStatus.Active,
})

export class BrokeragePipeline
  extends BrokeragePipelineRecord
  implements IBrokeragePipeline
{
  public static fromJS(initialState?: Partial<IBrokeragePipeline>) {
    if (!initialState) {
      return new BrokeragePipeline()
    }

    const {
      associatedListing,
      associatedProperty,
      attachments,
      buyers,
      buyersTitleCompany,
      commissionSplit,
      criticalDates,
      deletedCriticalDates,
      leasePriceBreakdown,
      listing,
      listingAgents,
      listingFee,
      potentialProfits,
      progress,
      sellers,
      sellersTitleCompany,
      sellingAgents,
      sellingFee,
      ...rest
    } = initialState

    return new BrokeragePipeline({
      associatedListing: associatedListing
        ? AssociatedListing.fromJS(associatedListing)
        : new AssociatedListing(),
      associatedProperty: associatedProperty
        ? AssociatedProperty.fromJS(associatedProperty)
        : new AssociatedProperty(),
      attachments: Array.isArray(attachments)
        ? List(attachments.map((a) => Attachment.fromJS(a)))
        : List<Attachment>(),
      buyers: Array.isArray(buyers)
        ? List(buyers.map((b) => AssociatedContact.fromJS(b)))
        : List<AssociatedContact>(),
      buyersTitleCompany: buyersTitleCompany
        ? Company.fromJS(buyersTitleCompany)
        : undefined,
      commissionSplit: commissionSplit
        ? CommissionSplit.fromJS(commissionSplit)
        : new CommissionSplit(),
      criticalDates: Array.isArray(criticalDates)
        ? List(criticalDates.map((c) => Assignment.fromJS(c)))
        : List<Assignment>(),
      deletedCriticalDates: Array.isArray(deletedCriticalDates)
        ? List(deletedCriticalDates.map((c) => Assignment.fromJS(c)))
        : List<Assignment>(),
      leasePriceBreakdown: Array.isArray(leasePriceBreakdown)
        ? List(leasePriceBreakdown.map((l) => LeasePriceTerm.fromJS(l)))
        : List<LeasePriceTerm>(),
      listing: listing ? Listing.fromJS(listing) : new Listing(),
      listingAgents: Array.isArray(listingAgents)
        ? List(listingAgents.map((l) => AssociatedContact.fromJS(l)))
        : List<AssociatedContact>(),
      listingFee: listingFee
        ? BrokeragePipelineCommissionFee.fromJS(listingFee)
        : new BrokeragePipelineCommissionFee(),
      potentialProfits: Array.isArray(potentialProfits)
        ? List(potentialProfits.map((p) => PotentialProfit.fromJS(p)))
        : List<PotentialProfit>(),
      progress: BrokeragePipelineProgress.fromJS(progress),
      sellers: Array.isArray(sellers)
        ? List(sellers.map((s) => AssociatedContact.fromJS(s)))
        : List<AssociatedContact>(),
      sellersTitleCompany: sellersTitleCompany
        ? Company.fromJS(sellersTitleCompany)
        : undefined,
      sellingAgents: Array.isArray(sellingAgents)
        ? List(sellingAgents.map((s) => AssociatedContact.fromJS(s)))
        : List<AssociatedContact>(),
      sellingFee: listingFee
        ? BrokeragePipelineCommissionFee.fromJS(sellingFee)
        : new BrokeragePipelineCommissionFee(),
      ...rest,
    })
  }

  public readonly associatedListing: AssociatedListing
  public readonly associatedProperty: AssociatedProperty
  public readonly attachments: List<Attachment>
  public readonly buyerLender: string
  public readonly buyers: List<AssociatedContact>
  public readonly buyersTitleCompany?: Company
  public readonly closingDate: number
  public readonly commissionSplit: CommissionSplit
  public readonly creator: string
  public readonly criticalDates: List<Assignment>
  public readonly deletedCriticalDates: List<Assignment>
  public readonly earnestMoney: number
  public readonly earnestMoneyDepositDate?: number
  public readonly earnestMoneyLocation: string
  public readonly earnestMoneyWithdrawlDate?: number
  public readonly effectiveDateOfOffer?: number
  public readonly id: string
  public readonly is1031Exchange: boolean
  public readonly leasePriceBreakdown: List<LeasePriceTerm>
  public readonly listing: Listing
  public readonly listingAgents: List<AssociatedContact>
  public readonly listingFee: BrokeragePipelineCommissionFee
  public readonly name: string
  public readonly noListingAgent: boolean
  public readonly noSellingAgent: boolean
  public readonly potentialProfits: List<PotentialProfit>
  public readonly progress: BrokeragePipelineProgress
  public readonly salesPrice: number
  public readonly sellerMortgage: string
  public readonly sellers: List<AssociatedContact>
  public readonly sellersTitleCompany?: Company
  public readonly sellingAgents: List<AssociatedContact>
  public readonly sellingFee: BrokeragePipelineCommissionFee
  public readonly status: PipelineStatus

  constructor(props?: Partial<BrokeragePipeline>) {
    props ? super(props) : super()
  }
}
